// src/pages/HackathonRegistration.tsx
import { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  User,
  Mail,
  ShieldCheck,
  Briefcase,
  Trash2,
  CheckCircle2,
  CalendarDays,
  Users,
} from "lucide-react";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import { registerForHackathon, fetchHackathonDetail } from "../../api/hackathonRegistration";

type GradeOption = "A1" | "A2" | "B1" | "B2" | "C1" | "C2";
type RoleOption =
  | "Software Engineer"
  | "Business Analyst"
  | "DevOps Engineer"
  | "Data Scientist"
  | "Product Manager";

type Member = {
  id: string;
  name: string;
  email: string;
  grade: GradeOption | "";
  role: RoleOption | "";
};

const GRADE_OPTIONS: GradeOption[] = ["A1", "A2", "B1", "B2", "C1", "C2"];
const ROLE_OPTIONS: RoleOption[] = [
  "Software Engineer",
  "Business Analyst",
  "DevOps Engineer",
  "Data Scientist",
  "Product Manager",
];

const EVENT_TITLE = "Innovation Hackathon 2026";
const EVENT_DATE_STR = "Mar 25–27, 2026";

export default function HackathonRegistration() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user, token } = useAuth();

  const [maxTeamSize, setMaxTeamSize] = useState(5);

  useEffect(() => {
    if (!id) return;
    axios.get(`${process.env.REACT_APP_API_URL}/hackathon-events/${id}`)
      .then((res) => {
        const data = res.data?._id ? res.data : res.data?.data || res.data;
        if (data?.maxTeamSize) setMaxTeamSize(data.maxTeamSize);
      })
      .catch(() => {});
  }, [id]);

  type Step = "details" | "members" | "success";
  const [step, setStep] = useState<Step>("details");
  const [hackathonData, setHackathonData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Fetch hackathon details on mount
  useEffect(() => {
    const fetchData = async () => {
      if (!id || !token) return;
      try {
        const data = await fetchHackathonDetail(token, id);
        setHackathonData(data.data ?? data);
      } catch (err) {
        console.error("Failed to fetch hackathon details:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, token]);

  // Use hackathon data or defaults
  const eventTitle = hackathonData?.title || EVENT_TITLE;
  
  // Format date range from startDate and endDate
  const formatDateRange = (startDate: string | Date | undefined, endDate: string | Date | undefined) => {
    if (!startDate || !endDate) return EVENT_DATE_STR;
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const startMonth = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const endMonth = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const year = end.getFullYear();
      return `${startMonth}–${endMonth}, ${year}`;
    } catch {
      return EVENT_DATE_STR;
    }
  };
  
  const eventDateStr = formatDateRange(hackathonData?.startDate, hackathonData?.endDate);

  // Use logged-in user's details for team leader
  const leaderName = user?.name || "John Doe";
  const leaderEmail = user?.email || "john.doe@capgemini.com";
  const [leaderGrade, setLeaderGrade] = useState<GradeOption | "">("");
  const [leaderRole, setLeaderRole] = useState<RoleOption | "">("");
  const [teamName, setTeamName] = useState("");

  const [members, setMembers] = useState<Member[]>([]);
  const [newMember, setNewMember] = useState<Member>({
    id: crypto.randomUUID(),
    name: "",
    email: "",
    grade: "",
    role: "",
  });

  const teamSize = 1 + members.length;
  const teamSizeLabel = `${teamSize}/${maxTeamSize}`;
  const canAddMore = teamSize < maxTeamSize;
  const minTeamOk = teamSize >= 3;
  const detailsValid = leaderGrade !== "" && leaderRole !== "" && teamName.trim().length > 0;

  const goBackToOverview = () => navigate(`/user-dashboard/hackathon-detail/${id}`);
  const goToList = () => navigate("/user-dashboard/hackathons");

  const addMember = () => {
    if (!canAddMore) return;
    if (!newMember.name || !newMember.email || !newMember.grade || !newMember.role) return;
    if (!newMember.email.endsWith("@capgemini.com")) {
      alert("Only Capgemini email addresses are accepted (e.g. example@capgemini.com)");
      return;
    }
    setMembers((p) => [...p, newMember]);
    setNewMember({ id: crypto.randomUUID(), name: "", email: "", grade: "", role: "" });
  };
  const removeMember = (id: string) => setMembers((p) => p.filter((m) => m.id !== id));

  const submit = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("You must be logged in to register.");
        return;
      }

      const payload = {
        hackathonId: id!,
        teamName,
        leaderGrade,
        leaderRole,
        members: members.map((m) => ({
          name: m.name,
          email: m.email,
          grade: m.grade,
          jobRole: m.role,
        })),
      };

      const response = await registerForHackathon(token, payload);

      console.log("✅ Registration success:", response);

      setStep("success");
    } catch (err: any) {
      console.error(err);
      alert(err.message || "Something went wrong during registration");
    }
  };

  const TopRibbon = () => (
    <div className="w-full border-b border-[#E0E4F0] bg-white/80 backdrop-blur">
      <div className="flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-2 text-sm">
          <button onClick={goBackToOverview} className="inline-flex items-center gap-2 text-[#0058AB] hover:underline">
            <ArrowLeft className="h-4 w-4" />
            Back to Overview
          </button>
          <span className="text-gray-400">|</span>
          <span className="font-medium text-gray-800">{eventTitle}</span>
          <span className="text-gray-400">|</span>
          <span className="text-gray-600 capitalize">
            {step === "details" ? "Details" : step === "members" ? "Members" : "Submitted"}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-600">Team Size</span>
          <span className="inline-flex items-center justify-center rounded-lg bg-[#EBF0FF] px-3 py-1 text-sm font-semibold text-[#0058AB]">
            {teamSizeLabel}
          </span>
        </div>
      </div>
    </div>
  );

  const SectionHeader = () => (
    <div className="flex items-center justify-between rounded-t-2xl bg-gradient-to-r from-[#F1F6FF] to-[#E9F3FF] px-5 py-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Team Registration</h2>
        <p className="text-sm text-gray-600">
          {eventTitle} · {eventDateStr}
        </p>
      </div>
      <div className="hidden md:flex items-center gap-2 text-sm">
        <span className="text-gray-600">Team Size</span>
        <span className="rounded-lg bg-[#EBF0FF] px-3 py-1 font-semibold text-[#0058AB]">{teamSizeLabel}</span>
      </div>
    </div>
  );

  const DetailsView = () => (
    <div className="rounded-2xl border border-[#DCE3F0] bg-white shadow-sm">
      <SectionHeader />

      <div className="space-y-4 border-b border-[#EDEFF5] p-5">
        <h3 className="text-sm font-semibold text-gray-800">Team Leader Details</h3>
        <p className="text-xs text-gray-500">
          Name and email are auto‑filled from your SSO profile. Complete your grade and role below.
        </p>

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Full Name</label>
            <div className="flex items-center gap-2 rounded-lg border border-[#E0E4F0] bg-gray-50 px-3 py-2">
              <User className="h-4 w-4 text-gray-400" />
              <input value={leaderName} readOnly className="w-full bg-transparent text-sm text-gray-700 outline-none" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Email Address</label>
            <div className="flex items-center gap-2 rounded-lg border border-[#E0E4F0] bg-gray-50 px-3 py-2">
              <Mail className="h-4 w-4 text-gray-400" />
              <input value={leaderEmail} readOnly className="w-full bg-transparent text-sm text-gray-700 outline-none" />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Your Grade*</label>
            <div className="flex items-center gap-2 rounded-lg border border-[#E0E4F0] bg-white px-3 py-2">
              <ShieldCheck className="h-4 w-4 text-gray-400" />
              <select
                value={leaderGrade}
                onChange={(e) => setLeaderGrade(e.target.value as GradeOption)}
                className="w-full bg-transparent text-sm text-gray-700 outline-none"
              >
                <option value="" disabled>
                  Select grade
                </option>
                {GRADE_OPTIONS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Your Job Role*</label>
            <div className="flex items-center gap-2 rounded-lg border border-[#E0E4F0] bg-white px-3 py-2">
              <Briefcase className="h-4 w-4 text-gray-400" />
              <select
                value={leaderRole}
                onChange={(e) => setLeaderRole(e.target.value as RoleOption)}
                className="w-full bg-transparent text-sm text-gray-700 outline-none"
              >
                <option value="" disabled>
                  Select role
                </option>
                {ROLE_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3 p-5">
        <h3 className="text-sm font-semibold text-gray-800">Team Information</h3>
        <p className="text-xs text-gray-500">Choose a unique name that represents your team’s identity.</p>

        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600">Team Name*</label>
          <input
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            placeholder="e.g., Code Crusaders"
            className="w-full rounded-lg border border-[#E0E4F0] bg-white px-3 py-2 text-sm text-gray-800 outline-none placeholder:text-gray-400"
          />
        </div>
      </div>
    </div>
  );

  const MembersView = () => (
    <div className="rounded-2xl border border-[#DCE3F0] bg-white shadow-sm">
      <SectionHeader />

      {/* Leader quick row */}
      <div className="border-b border-[#EDEFF5] p-5">
        <h3 className="mb-3 text-sm font-semibold text-gray-800">Team Leader Details</h3>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex items-center gap-2 rounded-lg border border-[#E0E4F0] bg-gray-50 px-3 py-2">
            <User className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-700">{leaderName}</span>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-[#E0E4F0] bg-gray-50 px-3 py-2">
            <Mail className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-700">{leaderEmail}</span>
          </div>
        </div>
      </div>

      {/* Members list + add */}
      <div className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Team Members</h3>
          <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700">{teamSizeLabel} members</span>
        </div>
        <p className="mb-4 text-xs text-gray-500">
          {members.length === 0
            ? "No members added yet. You need at least 2 more to form a valid team."
            : "Add more members (up to 5 including you). You need at least 2 members besides you."}
        </p>

        {members.length > 0 && (
          <div className="mb-4 overflow-hidden rounded-xl border border-[#E0E4F0]">
            <table className="w-full table-auto text-left">
              <thead className="bg-[#F8FAFF] text-xs text-gray-600">
                <tr>
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Grade</th>
                  <th className="px-3 py-2">Role</th>
                  <th className="px-3 py-2">Remove</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {members.map((m, i) => (
                  <tr key={m.id} className="border-t border-[#F0F2F7]">
                    <td className="px-3 py-2">{i + 1}</td>
                    <td className="px-3 py-2">{m.name}</td>
                    <td className="px-3 py-2">{m.email}</td>
                    <td className="px-3 py-2">{m.grade}</td>
                    <td className="px-3 py-2">{m.role}</td>
                    <td className="px-3 py-2">
                      <button onClick={() => removeMember(m.id)} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-red-600 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add member */}
        <div className="rounded-xl border border-[#E0E4F0] bg-white p-4">
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
            <input
              value={newMember.name}
              onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
              placeholder="e.g., Jane Smith"
              className="rounded-lg border border-[#E0E4F0] px-3 py-2 text-sm outline-none"
            />
            <input
              value={newMember.email}
              onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
              placeholder="e.g., jane.smith@capgemini.com"
              className="rounded-lg border border-[#E0E4F0] px-3 py-2 text-sm outline-none"
            />
            <select
              value={newMember.grade}
              onChange={(e) => setNewMember({ ...newMember, grade: e.target.value as GradeOption })}
              className="rounded-lg border border-[#E0E4F0] px-3 py-2 text-sm outline-none"
            >
              <option value="" disabled>
                Member Grade
              </option>
              {GRADE_OPTIONS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
            <select
              value={newMember.role}
              onChange={(e) => setNewMember({ ...newMember, role: e.target.value as RoleOption })}
              className="rounded-lg border border-[#E0E4F0] px-3 py-2 text-sm outline-none"
            >
              <option value="" disabled>
                Member Job Role
              </option>
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <button
              onClick={() =>
                setNewMember({ id: crypto.randomUUID(), name: "", email: "", grade: "", role: "" })
              }
              className="text-sm text-gray-600 hover:underline"
            >
              Cancel
            </button>
            <button
              disabled={!canAddMore}
              onClick={addMember}
              className={`inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium text-white ${canAddMore ? "bg-[#0058AB] hover:bg-[#004a90]" : "bg-gray-300"
                }`}
            >
              + Confirm & Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const SuccessView = () => (
    <div className="relative overflow-hidden rounded-2xl border border-[#DCE3F0] bg-white shadow-sm">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(#E1EEFF_1px,transparent_1px)] [background-size:16px_16px]" />
      <div className="relative space-y-6 p-6">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-6 w-6 text-green-600" />
          <span className="rounded-lg bg-green-50 px-3 py-1 text-sm font-medium text-green-700">
            Registration Successful!
          </span>
        </div>

        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Your Team is In! 🎉</h2>
          <p className="text-gray-700">
            <span className="font-semibold">{teamName || "Your team"}</span> has been successfully
            registered for <span className="font-semibold">{eventTitle}</span>.
          </p>
        </div>

        {/* Summary */}
        <div className="rounded-xl border border-[#E0E4F0]">
          <div className="flex items-center justify-between rounded-t-xl bg-[#F8FAFF] px-4 py-3">
            <span className="text-sm font-semibold text-gray-800">Registration Summary</span>
            <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700">
              Confirmed
            </span>
          </div>
          <div className="grid gap-4 p-4 md:grid-cols-3">
            <div className="rounded-lg bg-[#F8FAFF] p-3">
              <p className="text-xs text-gray-500">Team Name</p>
              <p className="text-sm font-medium text-gray-900">{teamName || "-"}</p>
            </div>
            <div className="rounded-lg bg-[#F8FAFF] p-3">
              <p className="text-xs text-gray-500">Team Size</p>
              <p className="text-sm font-medium text-gray-900">{teamSize} members</p>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-[#F8FAFF] p-3">
              <CalendarDays className="h-4 w-4 text-[#0058AB]" />
              <div>
                <p className="text-xs text-gray-500">Event Date</p>
                <p className="text-sm font-medium text-gray-900">{eventDateStr}</p>
              </div>
            </div>
          </div>

          {/* Leader + Members */}
          <div className="border-t border-[#EDEFF5] p-4">
            <p className="mb-2 text-sm font-semibold text-gray-800">Team Leader</p>
            <div className="flex items-center justify-between rounded-lg border border-[#E0E4F0] p-3">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0058AB] text-xs font-semibold text-white">
                  {leaderName
                    .split(" ")
                    .map((n) => n[0])
                    .join("")}
                </div>
                <div className="text-sm">
                  <p className="font-medium text-gray-900">{leaderName}</p>
                  <p className="text-gray-600">{leaderEmail}</p>
                </div>
              </div>
              <div className="text-right text-xs">
                <p className="text-gray-700">
                  Grade <span className="font-semibold">{leaderGrade || "-"}</span>
                </p>
                <p className="text-gray-700">
                  Role <span className="font-semibold">{leaderRole || "-"}</span>
                </p>
              </div>
            </div>

            <p className="mt-4 mb-2 text-sm font-semibold text-gray-800">
              Team Members ({members.length})
            </p>
            <div className="space-y-2">
              {members.map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-lg border border-[#E0E4F0] p-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#EBF0FF] text-xs font-semibold text-[#0058AB]">
                      {m.name ? m.name[0]?.toUpperCase() : "M"}
                    </div>
                    <div className="text-sm">
                      <p className="font-medium text-gray-900">{m.name}</p>
                      <p className="text-gray-600">{m.email}</p>
                    </div>
                  </div>
                  <div className="text-right text-xs">
                    <p className="text-gray-700">
                      Grade <span className="font-semibold">{m.grade}</span>
                    </p>
                    <p className="text-gray-700">
                      Role <span className="font-semibold">{m.role}</span>
                    </p>
                  </div>
                </div>
              ))}
              {members.length === 0 && (
                <div className="rounded-lg border border-dashed border-[#E0E4F0] p-4 text-center text-sm text-gray-600">
                  No team members added.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer CTAs */}
        <div className="flex flex-col-reverse items-stretch justify-between gap-3 pt-2 md:flex-row">
          <button
            onClick={goToList}
            className="inline-flex items-center justify-center rounded-lg border border-[#DDE3EF] bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Go to My Hackathons
          </button>
          <button
            onClick={goBackToOverview}
            className="inline-flex items-center justify-center rounded-lg bg-[#0058AB] px-4 py-2 text-sm font-medium text-white hover:bg-[#004a90]"
          >
            View Team Details
          </button>
        </div>
      </div>
    </div>
  );

  const ActionBar = () => {
    const canNext = detailsValid;
    const canSubmit = detailsValid && minTeamOk;

    return (
      <div className="mt-4 flex flex-col-reverse items-stretch justify-between gap-3 md:flex-row">
        <button
          onClick={goBackToOverview}
          className="inline-flex items-center justify-center rounded-lg border border-[#DDE3EF] bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>

        {step === "details" && (
          <button
            disabled={!canNext}
            onClick={() => setStep("members")}
            className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium text-white ${canNext ? "bg-[#0058AB] hover:bg-[#004a90]" : "bg-gray-300"
              }`}
          >
            Save & Continue
          </button>
        )}

        {step === "members" && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setStep("details")}
              className="inline-flex items-center justify-center rounded-lg border border-[#DDE3EF] bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Back
            </button>
            <button
              disabled={!canSubmit}
              onClick={submit}
              className={`inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium text-white ${canSubmit ? "bg-[#0058AB] hover:bg-[#004a90]" : "bg-gray-300"
                }`}
            >
              Submit Registration
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <DashboardLayout>
      {TopRibbon()}
      <div className="w-full px-6 py-6">
        {step === "details" && (
          <>
            {DetailsView()}
            {ActionBar()}
          </>
        )}
        {step === "members" && (
          <>
            {MembersView()}
            {ActionBar()}
          </>
        )}
        {step === "success" && SuccessView()}
      </div>
    </DashboardLayout>
  );
}